/**
 * Per-Signal landing-page content — the source data for /learn/<slug>
 * pages rendered by SignalLearnPage.tsx.
 *
 * Why a typed data file vs inline JSX per page: 12 pages all follow the
 * same structure (problem → manual method → Avium's approach → who it's
 * for → CTA). One data shape + one template = consistent structure +
 * cheap to add the next 9 pages in PR 3. Drift between pages becomes
 * impossible.
 *
 * Content alignment: the descriptions of HOW each Signal is computed
 * track `server/src/lib/intelligenceEngine.ts` exactly. If the engine's
 * algorithm changes (e.g. UTIL_OVER threshold moves), update both
 * places in the same commit. Same discipline as the carousel + tour.
 *
 * CLAUDE.md anchors: every page uses proper-noun "Avium Signals" and
 * "Avium Intelligence" (Critical Rule 3), maps to the Catch pillar
 * (Principle 4), and surfaces the persona language (scrum master /
 * agile coach / engineering manager) so the page ranks for the
 * Cluster E searches per the 2026-05-26 roadmap direction.
 */

import type { MarketingVisualKind } from '../components/marketing/marketingVisualData';

export interface SignalLearnSection {
  heading: string;
  body: string;
}

export interface SignalPersona {
  role: string;
  benefit: string;
}

export interface SignalLearnContent {
  slug: string;
  signalName: string;
  /** Used as the <h1> + page <title>. */
  pageTitle: string;
  /** ~155 chars for SEO snippet. */
  metaDescription: string;
  /** Hero subhead under the H1. */
  tagline: string;
  /** Tier badge to show — 'Team' if Signal-only, 'Business' if AI layer too. */
  tier: 'Team' | 'Business';
  /** Branded product visual under the hero — only set where the visual honestly
   *  represents this Signal (grounded/never-invented principle). */
  heroVisual?: MarketingVisualKind;

  problem: SignalLearnSection;
  manualMethod: SignalLearnSection & { painPoints: string[] };
  aviumApproach: SignalLearnSection & { mechanics: string[] };
  forWhom: SignalPersona[];
  cta: { heading: string; body: string };

  /** Other /learn slugs to cross-link in the "Related signals" footer. */
  relatedSlugs: string[];
}

export const SIGNAL_LEARN_CONTENT: Record<string, SignalLearnContent> = {
  'sprint-risk-forecast': {
    slug: 'sprint-risk-forecast',
    heroVisual: 'sprint-risk',
    signalName: 'Sprint Risk Forecast',
    pageTitle: 'Sprint Risk Forecast — predict sprint failure before it happens',
    metaDescription: "Avium Signals detects sprint risk by comparing time elapsed against work done, blockers, and over-allocation. Predict sprint failure before standup — no LLM key required.",
    tagline: "Stop finding out at sprint review. Avium Signals reads your Jira data — or any work management tool — and forecasts which sprints are about to miss — and why.",
    tier: 'Team',

    problem: {
      heading: 'The week-3 surprise problem',
      body: "Every scrum master has lived this: standup on day 2 of the sprint feels fine. Standup on day 8 feels fine. Day 10, you start sensing something's off, but the burndown chart still looks survivable. Day 12, suddenly half the tickets are blocked, two engineers are over capacity, and the sprint goal you committed to is unreachable. By sprint review you're explaining why three stories slipped — and the explanation is the same one as last sprint, and the sprint before that.\n\nThe data that would have caught this was in Jira the whole time. Nobody had a way to see it together.",
    },

    manualMethod: {
      heading: "How agile teams try to detect sprint risk today",
      body: "Most scrum masters and agile coaches build something to compensate. The shape varies; the pain doesn't.",
      painPoints: [
        "A spreadsheet of who's working on what, manually updated from Jira every Monday. Stale by Tuesday.",
        "A burndown chart that's accurate-ish, but lags reality by a day and doesn't account for blockers.",
        "A Slack reminder to ask each engineer 'how's it going' — relies on engineers volunteering bad news.",
        "Confluence-page sprint health summaries written by the SM the morning of sprint review — too late to act.",
        "Jira's built-in reports: Burndown, Velocity, Sprint Report. They tell you what HAPPENED. They don't tell you what's about to.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes sprint risk',
      body: "Sprint Risk is one of the patterns the Avium Signals engine watches every time the Insights page loads. It's deterministic — same data, same answer, no LLM call, no API key. The Signal fires on a combination of three measurable conditions:",
      mechanics: [
        "Pace gap: percent of work done vs percent of sprint elapsed. If 60% of the sprint is gone but only 35% of the work is done, that's a 25-point pace gap — material risk.",
        "Blocker count: number of tickets currently flagged or stuck in review with no recent activity (default threshold: 7 days no log AND no status change). One blocker is noise; four blockers concentrated in the same sprint is a pattern.",
        "Over-allocation: people with committed work above 105% of their capacity. Often the actual driver of risk — a sprint with healthy pace can still miss if the one person who can finish the last three tickets is already pulled six different directions.",
        "When any two of the three fire simultaneously, the Signal escalates severity. Avium's UI renders that on the Sprint Risk card and the daily AI Briefing surfaces it as a top-of-mind item.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "See the risk before standup, run a 5-minute conversation that surfaces what's blocking the team. Carry the action items into the daily — not the retro." },
      { role: 'Agile coaches', benefit: "Spot the patterns across the teams you coach. A team that hits Sprint Risk three sprints in a row has a planning problem, not a delivery problem; you can name it with data." },
      { role: 'Engineering managers', benefit: "Walk into the 1:1 with your TL knowing which sprint is at risk and why. Skip the 'how's it going' opener; lead with the specific blocker." },
      { role: 'VPs of engineering', benefit: "Roll up Sprint Risk across every team's active sprint into one number for the board update. Drill into the red ones; ignore the green." },
    ],

    cta: {
      heading: 'Run Sprint Risk on your own sprints',
      body: "Free tier connects to your work management tool (Jira today, more integrations on the way), pulls your active sprint, and shows Sprint Risk against your real tickets. No credit card. Avium Intelligence (the AI briefing layer on top) is available on Business; the Signal itself runs on Team.",
    },

    relatedSlugs: ['velocity-drift', 'over-allocation', 'blocked-and-stale'],
  },

  'velocity-drift': {
    slug: 'velocity-drift',
    heroVisual: 'velocity-drift',
    signalName: 'Velocity Drift',
    pageTitle: 'Velocity Drift — catch cliffs in your team velocity before they become trends',
    metaDescription: "Avium Signals detects velocity drift across recent sprints — cliffs (sharp drops) and gradual trend swings. Engineering managers see why velocity is changing before it shows up in the burn chart.",
    tagline: "Velocity is the metric every VP asks about and no team trusts. Avium Signals tells you when it's actually shifting — and whether the shift is structural or noise.",
    tier: 'Team',

    problem: {
      heading: "Why velocity charts mislead",
      body: "A velocity chart in Jira looks like a row of bars. They jiggle up and down. Some weeks are higher, some lower. Most teams have been told to ignore the jiggle, watch the rolling average. Fine — until the rolling average drops 15% over four sprints and nobody noticed because each individual sprint felt explainable.\n\nThe inverse problem also bites: a velocity drop from sprint 14 to sprint 15 looks dramatic on the chart, panic ensues, and a month later you realize it was holiday-week noise. The data didn't actually shift.",
    },

    manualMethod: {
      heading: 'How agile teams try to read velocity manually',
      body: "Reading a velocity chart by eye is a guess wearing a confidence costume. The common mistakes:",
      painPoints: [
        "Looking at the most recent two sprints, declaring a trend. Two data points isn't a trend.",
        "Ignoring variance — a team that ships 30/40/30/40/30 is steady; a team that ships 35/35/35/35/35 is also steady. The mean is the same; one is volatile and one is predictable. Burndown charts treat them identically.",
        "Calling a cliff (sprint 14 = 38, sprint 15 = 12) a 'bad sprint' instead of investigating: was it scope, was it people-out, was it a critical bug that consumed the team?",
        "Confusing capacity changes with velocity changes — half the team went on PTO, of course velocity dropped, but Jira's chart doesn't account for that.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes velocity drift',
      body: "Velocity Drift is a deterministic signal — pure math on your sprint history, no AI. Avium computes it from the last 6 closed sprints (configurable) and classifies the pattern into one of four buckets:",
      mechanics: [
        "Cliff: the most recent sprint completed less than 65% of the period's peak. A sharp, recent drop — the most demo-able 'something just broke' signal.",
        "Trend rising: the second-half mean of the window is more than 12% higher than the first-half mean. Capacity is growing or process is sharpening.",
        "Trend falling: the second-half mean is more than 12% below the first-half mean. Different from a cliff — a gradual decline, often because attrition or scope shift has been masked by sprint-to-sprint variance.",
        "Volatile: high coefficient of variation (standard deviation / mean) above 0.45. The team's output is unpredictable enough that planning around an average is a mistake.",
        "Stable: none of the above. A team that ships within ±12% sprint-to-sprint is doing the hard work right.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Bring data to retro instead of vibes. 'Our velocity is volatile' is a conversation starter; 'we shipped less' is a conversation ender." },
      { role: 'Agile coaches', benefit: "Compare drift patterns across the teams you support. A team's cliff usually correlates with a specific event (key engineer leaving, scope balloon); the chart makes the correlation visible." },
      { role: 'Engineering managers', benefit: "Honest planning conversations. If your team is volatile, commit less; if it's stably rising, commit more. Trying to plan a volatile team like a stable one is how you burn people out." },
      { role: 'VPs of engineering', benefit: "Aggregate drift across the org. Three teams with cliffs in the same quarter = an org-level pattern (a bad release, an incident response that consumed everyone, attrition wave). Surfaces structural issues vs team-specific noise." },
    ],

    cta: {
      heading: 'See velocity drift on your own sprints',
      body: "Avium reads your last 6 closed sprints from Jira (or any work management tool, on whatever cadence you've configured) and surfaces the drift classification on the Insights page within seconds of import. Free tier shows the Signal; the AI Briefing that contextualizes it into a plain-English narrative ('your team has been volatile for two months; here's the likely cause') is on Business.",
    },

    relatedSlugs: ['sprint-risk-forecast', 'sprint-scope-change', 'flow-efficiency'],
  },

  'over-allocation': {
    slug: 'over-allocation',
    heroVisual: 'over-allocation',
    signalName: 'Over-Allocation',
    pageTitle: 'Over-Allocation — the burnout signal hidden in your Jira data',
    metaDescription: "Avium Signals detects over-allocation by computing committed work vs each person's capacity. Spot the people stretched past 105% before the sprint slips and they burn out.",
    tagline: "Half your team is at 80%. Two engineers are at 130%. Your burndown chart can't tell you who.",
    tier: 'Team',

    problem: {
      heading: "Why burnout is invisible in the standard tools",
      body: "Jira's sprint view shows you the work. It doesn't show you who has too much of it. The sprint can be on track while one engineer is silently drowning, and the only way you find out is when they take an unscheduled PTO day or quit.\n\nMost teams approximate with a heuristic — 'don't load anyone with more than X story points' — but story points aren't comparable across people, and 'too much work' is invisible if it sits across multiple Jira projects, multiple sprints, multiple priorities. The person who appears once in each of six places looks fine on every individual board and unsurvivable on the aggregate.",
    },

    manualMethod: {
      heading: "How agile teams try to spot over-allocation today",
      body: "Real moves teams make. None scale.",
      painPoints: [
        "1:1s where the manager asks 'how's your load' and the engineer says 'fine' because saying anything else feels like failing.",
        "A spreadsheet that lists every person and their assigned story points, updated weekly. Stale within hours, never reflects in-flight unplanned work.",
        "'Capacity planning' as a sprint-planning event — committed to and immediately violated as soon as the first urgent bug lands.",
        "Watching for symptoms instead of causes: late commits, missed standups, terse Slack messages. By the time symptoms appear, the over-allocation has been going on for weeks.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes over-allocation',
      body: "Over-Allocation is a per-person calculation done at the same moment as the sprint risk computation. The math is straightforward; the value is that it actually runs:",
      mechanics: [
        "Capacity per person: sum of their per-sprint hours (or story points, configurable per org). Set in Avium's resource list — defaults to 40h, adjustable for FTE, PTO windows, and mid-sprint capacity changes.",
        "Committed per person: sum of planned hours (or points) across every active-sprint ticket they're assigned to. Avium counts cross-project work — the engineer assigned to one Platform ticket AND two Mobile tickets gets credit for all three.",
        "Utilization percent = committed / capacity × 100. Avium classifies the result: over (>105%), high (90–105%), healthy (55–90%), under (<55%).",
        "The Over-Allocation Signal lists every person in the 'over' bucket, sorted by severity. The Avium Intelligence briefing turns that into 'Sarah is at 130%; she has 2 blocking tickets that should move to Mike (currently 60%).'",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Walk into sprint planning with a list of who's already maxed. Push back on adding more to those people before commitments get made. Saves the awkward mid-sprint conversation." },
      { role: 'Agile coaches', benefit: "When you coach a team through 'why we keep missing,' over-allocation is one of the top three answers. Make the pattern visible instead of explaining it abstractly." },
      { role: 'Engineering managers', benefit: "1:1 prep that doesn't require asking. You walk in knowing your TL is 30% over; the conversation skips the warmup and goes to redistribution." },
      { role: 'VPs of engineering', benefit: "Org-level utilization. If a department is averaging 110% while another is at 55%, that's a structural hiring or scope problem — visible at the rollup level without drilling into individuals." },
    ],

    cta: {
      heading: 'See over-allocation across your team',
      body: "Free tier shows you who's over, by how much, and which tickets are driving it. Avium Intelligence (Business) adds the rebalancing suggestion — which person has spare capacity, which tickets to move, and what the result would look like.",
    },

    relatedSlugs: ['sprint-risk-forecast', 'velocity-drift', 'hand-off-cost'],
  },

  'blocked-and-stale': {
    slug: 'blocked-and-stale',
    heroVisual: 'intelligence-briefing',
    signalName: 'Blocked & Stale Tickets',
    pageTitle: 'Blocked & Stale — surface the work nobody flagged',
    metaDescription: "Avium Signals detects blocked and stale tickets in Jira or any work management tool — flagged work, items stuck in review past 7 days, no recent activity. The standup talking points that don't surface until the sprint slips.",
    tagline: "Half your sprint is fine. A few tickets have been quietly stuck for a week. Avium tells you which ones — by age, with the reason — before standup.",
    tier: 'Team',

    problem: {
      heading: "The blocker that nobody calls a blocker",
      body: "Engineers don't like saying they're blocked. It feels like failure. So the ticket sits — in review, in QA, waiting on a decision that never came — and the only signal is silence. The standup goes on. The burndown chart looks fine because the ticket is still 'in progress.' Three days later a different engineer mentions the blocker offhand and the whole team realizes nobody was tracking it.\n\nThe data was always there. Jira knew the ticket hadn't moved in seven days. Nobody asked Jira.",
    },

    manualMethod: {
      heading: "How teams try to catch blockers today",
      body: "The methods that don't scale:",
      painPoints: [
        "Manually checking the 'flagged' filter every morning — works if anyone remembers to flag tickets, which is the entire problem.",
        "Tracking 'in review' columns by eye — your eye lies. A column with 6 tickets feels normal even if half of them have been there for a week.",
        "Asking in standup 'anyone blocked' — only catches the engineers willing to say so.",
        "Watching the burndown chart for inflection points — by the time the chart shows it, three days of work have already been lost.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes blocked & stale',
      body: "Avium reads ticket activity (status changes, time logs, assignee changes) and applies a small set of rules:",
      mechanics: [
        "Flagged: any ticket with the Jira flagged field set. Always surfaced regardless of age — the engineer asked for help; that's the loudest signal.",
        "Stuck in review: ticket in a phase named 'review' (Avium's status mapping detects this) with no time-log activity for 7+ days. Configurable threshold.",
        "Stale in-progress: ticket in an in-progress phase for 14+ days AND no activity for 7+ days. Catches the 'started, then forgotten' pattern.",
        "Sorted by age descending — the oldest blocker is the first thing to discuss at standup. That's the agenda.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Your standup running order, sorted. Skip the warmup; lead with the oldest blocker." },
      { role: 'Agile coaches', benefit: "When you see the same ticket blocked across three standups, that's a coaching moment — escalation should have happened by day two." },
      { role: 'Engineering managers', benefit: "Knows who to unblock without asking. The list is on your phone before the team's daily." },
      { role: 'VPs of engineering', benefit: "Cross-team blocker count is one of the highest-leverage org-health metrics. If it's climbing, something structural is off (dependency on a slow team, an external vendor, a single point of failure)." },
    ],

    cta: {
      heading: 'See your stuck work',
      body: "Free tier surfaces blocked + stale tickets in real time once your work management tool is connected (Jira today, more integrations on the way). Threshold configurable. Avium Intelligence on Business turns the list into a prioritized briefing that names who to escalate to.",
    },

    relatedSlugs: ['sprint-risk-forecast', 'time-in-status', 'hand-off-cost'],
  },

  'cross-sprint-blocked': {
    slug: 'cross-sprint-blocked',
    heroVisual: 'intelligence-briefing',
    signalName: 'Cross-Sprint Blockers',
    pageTitle: 'Cross-Sprint Blockers — the blocker that never showed up on your board',
    metaDescription: "Avium Signals detects when a committed sprint ticket is blocked by unfinished work in another sprint or the backlog — the dependency that isn't on your board, so it slips silently. Surfaced before the commitment stalls.",
    tagline: "Your sprint ticket is waiting on a ticket that isn't in your sprint. It's not on your board, so nobody's watching it — until the commitment slips. Avium watches it for you.",
    tier: 'Team',

    problem: {
      heading: "The blocker that isn't on your board",
      body: "You can see everything on this sprint's board. That's exactly why cross-sprint blockers are so dangerous: the thing blocking you isn't there. A committed ticket depends on work that lives in a future sprint, an earlier sprint that spilled over, or the backlog — and because that blocker never shows up in front of the team, nobody's tracking it.\n\nThe ticket sits at 'in progress,' the burndown looks fine, and the dependency quietly ages out of sight. You find out at the sprint review, when the committed work didn't land because the thing it needed was never finished — and was never anyone's job this sprint.",
    },

    manualMethod: {
      heading: "How teams try to catch cross-sprint dependencies today",
      body: "The methods that don't scale:",
      painPoints: [
        "Reading every 'is blocked by' link by hand — Jira stores the link, but nobody opens 40 tickets each morning to walk the dependency chain.",
        "Trusting that the blocker's team will deliver on time — their sprint is invisible to you, and their priorities aren't yours.",
        "Catching it in sprint planning — planning checks what's IN the sprint, not what the sprint silently depends on elsewhere.",
        "Finding out at the review — by then the commitment has already slipped, and 'we were blocked' lands as a surprise.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes cross-sprint blockers',
      body: "Avium already ingests Jira issue links as a typed dependency graph (oriented so the blocker is always the source). For each open ticket in the active sprint, it walks the 'blocked by' edges and applies two tests:",
      mechanics: [
        "Is the blocker unfinished? A blocker that's already Done is no risk — it's filtered out.",
        "Is the blocker outside this sprint? If it lives in another sprint or the backlog, it's a cross-sprint blocker — the silent kind. Same-sprint blockers are the ordinary 'blocked' Signal.",
        "Backlog blockers rank first — a dependency on un-scheduled work is the most stranded, so it leads the list.",
        "Severity is never just 'info' — a committed item gated on work the team doesn't own this sprint is at least a warning, and three or more is critical.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "The blockers your standup can't see, surfaced before the daily — with the upstream ticket and whose it is, so you can chase the right owner." },
      { role: 'Project managers', benefit: "Cross-project dependencies are where delivery dates die. This is the early warning that another team's slip is about to become yours." },
      { role: 'Engineering managers', benefit: "Know which of your commitments hinge on work outside your team — the conversation to have with the other lead today, not at the review." },
      { role: 'VPs of engineering', benefit: "A rising cross-sprint blocker count is a structural signal: teams are too tightly coupled, or one team is a bottleneck the whole org waits on." },
    ],

    cta: {
      heading: 'See what your sprint is really waiting on',
      body: "Connect your work management tool (Jira today) and Avium maps your dependency graph automatically. The cross-sprint blocker Signal surfaces on the free tier; Avium Intelligence on Business folds it into a prioritized briefing that names the upstream owner to chase.",
    },

    relatedSlugs: ['blocked-and-stale', 'sprint-risk-forecast', 'hand-off-cost'],
  },

  'sprint-scope-change': {
    slug: 'sprint-scope-change',
    signalName: 'Sprint Scope Change',
    pageTitle: 'Sprint Scope Change — track mid-sprint adds and removes with receipts',
    metaDescription: "Avium Signals tracks tickets added and removed mid-sprint. The receipt scrum masters bring to retro when leadership asks why the commitment slipped.",
    tagline: "'We committed to X, we shipped Y' is half the conversation. 'Here's what got added on day 6 and what got dropped on day 8' is the other half. Avium has it.",
    tier: 'Team',

    problem: {
      heading: "The retro nobody wins",
      body: "Sprint review. You committed to 25 story points. You shipped 18. Leadership wants to know why. The team starts naming things: 'a P0 came in,' 'we lost two days on a regression,' 'one ticket grew.' Each one feels reasonable individually; in aggregate, you missed by 28%. Nobody can prove which factor mattered most.\n\nWithout a record, every retro is anecdote. With a record, the conversation shifts from 'who's to blame' to 'how do we estimate scope-change risk into the next sprint.'",
    },

    manualMethod: {
      heading: "How teams try to track scope change today",
      body: "Methods that work for one sprint and decay by the third:",
      painPoints: [
        "A spreadsheet started at sprint planning — by week 2, nobody updates it.",
        "Jira's Sprint Report shows added/removed totals as numbers but not as a list of WHICH tickets — useless when leadership wants specifics.",
        "Asking the team at retro: 'what got added' — the team forgets. Memory is compressed.",
        "Eyeballing the sprint burndown for upward kinks: tells you SOMETHING changed but not what.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes sprint scope change',
      body: "Avium reads the sprint field change history on every ticket. Every ticket added to or removed from a sprint after the sprint started is logged.",
      mechanics: [
        "Added mid-sprint: tickets whose sprintId changed to the current sprint AFTER the sprint's startDate. Lists ticket key + summary + when it was added.",
        "Removed mid-sprint: tickets whose sprintId was the current sprint at start but no longer is. Same detail.",
        "Net scope delta: added story points minus removed story points. Positive = scope grew; negative = team de-scoped to make it.",
        "Net scope creep percentage: delta as a percent of the original commit. Avium escalates the Signal when creep exceeds 15%.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Retro evidence. Walk in with the list of what changed. The conversation goes from 'we missed' to 'here's the data on scope dynamics.'" },
      { role: 'Agile coaches', benefit: "Coach the planning practice when scope creep is structural. A team that adds 20% every sprint isn't bad at execution; it's bad at planning. The data names which one." },
      { role: 'Engineering managers', benefit: "Push back on mid-sprint adds with evidence. 'We added X points to your sprint last time and you missed by Y' is a harder conversation to lose." },
      { role: 'VPs of engineering', benefit: "Cross-team scope-change patterns. If one PM is consistently adding mid-sprint and that team consistently misses, the leadership conversation has a name on it." },
    ],

    cta: {
      heading: 'See what changed in your last sprint',
      body: "Free tier shows the scope-change signal on demo data. Once your work management tool is connected (Jira today, more integrations on the way), Avium reads sprint history and surfaces the deltas immediately. Available on Team and above.",
    },

    relatedSlugs: ['sprint-risk-forecast', 'velocity-drift', 'data-quality-score'],
  },

  'data-quality-score': {
    slug: 'data-quality-score',
    signalName: 'Data Quality Score',
    pageTitle: 'Data Quality Score — know how much to trust your sprint forecast',
    metaDescription: "Avium Signals scores your Jira data quality 0-100 — or any work management tool — based on missing assignees, unestimated tickets, undated work, and orphan sprints. Tells you how much to trust the rest of the analytics.",
    tagline: "Your forecast is reading from your data. If your data is broken, the forecast is too. Avium scores the gap.",
    tier: 'Team',

    problem: {
      heading: "Garbage in, garbage out — quantified",
      body: "Every team's Jira data has gaps. Unassigned tickets, missing estimates, tickets with no sprint, tickets that should have been closed but were forgotten. Most teams have learned to ignore the gaps; the analytics ignore them too and confidently report numbers they shouldn't trust.\n\nThe insidious version: a velocity chart that looks great because half the team's work is in 'unestimated' tickets that don't count. Or a sprint forecast that says you're on track because the four blocked tickets have no due date.",
    },

    manualMethod: {
      heading: "How teams try to keep Jira clean today",
      body: "The familiar treadmill:",
      painPoints: [
        "'Jira hygiene' meetings — well-intentioned, attended once, ignored thereafter.",
        "Periodic audits where the scrum master combs the backlog manually. Catches 60% of issues; misses the systemic ones.",
        "Required-fields rules in Jira itself — works at creation time, but tickets created in bulk or imported from other systems bypass them.",
        "Trusting the analytics anyway because there's no other choice. The dashboard shows a number; you quote it; nobody investigates the foundation it sits on.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes data quality',
      body: "Avium runs through every open ticket and counts gaps in the fields that matter for analytics. The score is a weighted aggregate, 0-100, where 100 is clean.",
      mechanics: [
        "Missing assignee — heaviest weight (1.0). An unassigned ticket has no person to attribute capacity to; corrupts both sprint risk and over-allocation signals.",
        "Unestimated — weight 0.8. Tickets without story points or planned hours can't be counted in velocity or capacity.",
        "Missing story points (Jira-imported tickets only) — weight 0.6. Separate from unestimated to flag the 'we use points except sometimes' inconsistency.",
        "No sprint — weight 0.5. A ticket that exists outside any sprint isn't being planned; in a points-based shop that distorts velocity reads.",
        "Overdue — weight 1.0. Tickets whose due date passed are either done-and-not-closed (data lie) or genuinely late (operational issue). Either way, surface.",
        "Score = 100 minus the sum of (issue weight × percent of open tickets affected). Clamped to [0, 100].",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "A standing standup item: 'data quality 78/100, fix these 3 things this week.' Builds the team hygiene habit without nagging." },
      { role: 'Agile coaches', benefit: "Quantifies the 'your Jira is a mess' coaching conversation. 0-100 is concrete; 'your Jira is a mess' is abstract." },
      { role: 'Engineering managers', benefit: "Tells you how much to trust your forecast. Below 60 = treat the forecast as directional, not predictive." },
      { role: 'VPs of engineering', benefit: "Cross-team data quality is a leading indicator of cross-team analytics trust. If one team's score is 90 and another is 50, you can't compare them on the same metric." },
    ],

    cta: {
      heading: 'See your data quality score',
      body: "Free tier shows the score and the top gaps the first time you load data. The Avium Intelligence layer on Business turns the gap list into a prioritized cleanup queue.",
    },

    relatedSlugs: ['sprint-risk-forecast', 'reopen-rate', 'sprint-scope-change'],
  },

  'reopen-rate': {
    slug: 'reopen-rate',
    heroVisual: 'workflow-flow',
    signalName: 'Reopen Rate',
    pageTitle: 'Reopen Rate — the quality signal Jira hides',
    metaDescription: "Avium Signals tracks how often tickets bounce back from Done. The leading quality + acceptance indicator that Jira's default reports don't surface.",
    tagline: "The percent of your Done column that doesn't stay done. Tells you whether your team is shipping work or shipping work twice.",
    tier: 'Team',

    problem: {
      heading: "Why Done isn't done",
      body: "A ticket gets moved to Done. Two days later it comes back — reopened because the QA caught something, or the customer reported a regression, or the definition of done was looser than everyone thought. The team's velocity chart still counts the original close. The cycle-time chart looks fast. Nothing in Jira's stock reporting says 'you shipped this twice.'\n\nReopen rate is the cleanest leading indicator of acceptance criteria erosion. When it climbs, it climbs quietly — sprint by sprint, retroes don't notice, until quality becomes a topic at the all-hands.",
    },

    manualMethod: {
      heading: "How teams try to track quality today",
      body: "What teams try, what doesn't quite work:",
      painPoints: [
        "Counting reopened bugs via Jira labels — only works if QA labels consistently, which is rare.",
        "Watching the in-progress column for tickets that 'reappear' — eye-driven, no record of how often it happens.",
        "Tracking escaped-defects by post-release bug count — too late, and conflates regressions with new defects.",
        "Asking QA in retro 'how was quality this sprint' — vibes-based, never compared sprint-to-sprint.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes reopen rate',
      body: "Avium reads ticket status transition history (TicketTransition table). Any ticket that moves from a Done status to a non-Done status is counted as a reopen.",
      mechanics: [
        "Reopens this window: count of tickets reopened in the last N closed sprints (default: 6).",
        "Reopen rate: reopens divided by total tickets moved to Done in the same window. Reported as a percentage.",
        "Trend: comparing the first half of the window to the second. 'Reopen rate trending up' is the Signal that escalates the card severity.",
        "Per-person breakdown: who's filing the reopens (QA) vs who's the original assignee. Surfaces handoff patterns.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Retro discussion point. Talking about 'why did 3 tickets reopen this sprint' is more useful than talking about 'how was the sprint.'" },
      { role: 'Agile coaches', benefit: "Definition-of-done erosion is a coaching pattern. Reopen-rate trend is the data that names it." },
      { role: 'Engineering managers', benefit: "Quality conversations with your TLs that aren't anecdotal. The number is the number." },
      { role: 'VPs of engineering', benefit: "Compare quality across teams without subjective input. A team with 5% reopen is operating differently than one with 25%, even if both have the same velocity." },
    ],

    cta: {
      heading: 'See your reopen rate',
      body: "Free tier surfaces reopen rate the first time you load sprint history. Trend detection across multiple sprints unlocks on Team; the AI Briefing's quality narrative ('your reopen rate has climbed 8 points over two months — here's the pattern') is on Business.",
    },

    relatedSlugs: ['data-quality-score', 'flow-efficiency', 'hand-off-cost'],
  },

  'flow-efficiency': {
    slug: 'flow-efficiency',
    heroVisual: 'workflow-flow',
    signalName: 'Flow Efficiency',
    pageTitle: 'Flow Efficiency — the active vs waiting time of your tickets',
    metaDescription: "Avium Signals computes flow efficiency — what percentage of cycle time was active work vs. waiting in queues. The Lean staple that exposes hand-off and approval bottlenecks.",
    tagline: "A ticket takes 8 days to ship. Two of those days are someone working on it. Six are waiting. Avium tells you which is which.",
    tier: 'Team',

    problem: {
      heading: "Where time actually goes",
      body: "Cycle time is the easy metric — how long from start to done. It hides the most actionable detail: of the 8 days a ticket sat in flight, only 2 were active development. The other 6 were code review queues, QA queues, waiting for a stakeholder decision, waiting for a deploy window.\n\nFlow efficiency is the Lean ratio that exposes this gap. Teams stuck below 25% are often debugging the wrong problem — they're hiring engineers when the bottleneck is the approval queue.",
    },

    manualMethod: {
      heading: "How teams try to read process bottlenecks today",
      body: "Hard to do by hand:",
      painPoints: [
        "Eyeballing the in-progress column for tickets that 'feel slow' — no comparison, no trend.",
        "Counting time-in-status from Jira's history view — works for one ticket; doesn't scale.",
        "Process-mining tools (Celonis et al.) — overkill, enterprise pricing, never adopted by scrum teams.",
        "Cycle time charts — tell you HOW LONG but not WHERE THE TIME WENT.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes flow efficiency',
      body: "Avium reads every ticket's phase transition history and time-log entries.",
      mechanics: [
        "Active time: sum of time logged on the ticket (or, when no time logs exist, the time spent in phases tagged as in-progress or development).",
        "Total time: time elapsed from first non-backlog transition to first done transition.",
        "Flow efficiency = active / total. Reported as a percentage.",
        "Per-phase dwell: breakdown of where total time was spent. Reveals the specific bottleneck (e.g., 'code review averages 3.2 days; engineering averages 1.1 days; the slowness isn't in writing code').",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "When the team says 'we're slow,' you can name what kind of slow. Active-slow needs different intervention than queue-slow." },
      { role: 'Agile coaches', benefit: "Lean coaching with data. Flow efficiency below 25% is a well-known threshold for 'your process has more queues than execution.' Show the team the number." },
      { role: 'Engineering managers', benefit: "Direct the next process improvement at the actual bottleneck. If review is the queue, hire reviewers or change the review rule; don't hire more engineers." },
      { role: 'VPs of engineering', benefit: "Cross-team flow efficiency is the most underused metric in engineering leadership. A 15-point gap between two teams almost always traces to specific process choices." },
    ],

    cta: {
      heading: 'See your flow efficiency',
      body: "Avium computes flow efficiency the first time you connect your work management tool (Jira today, more integrations on the way). Per-phase breakdown is on Team; the AI Briefing layer on Business explains the bottleneck in plain English.",
    },

    relatedSlugs: ['time-in-status', 'hand-off-cost', 'workflow-sankey'],
  },

  'hand-off-cost': {
    slug: 'hand-off-cost',
    heroVisual: 'workflow-flow',
    signalName: 'Hand-off Cost',
    pageTitle: 'Hand-off Cost — the hidden tax in your assignee changes',
    metaDescription: "Avium Signals tracks how many engineers touch each ticket. Hand-off count predicts cycle time and reopen rate more reliably than story points do.",
    tagline: "A ticket assigned to one person ships fast. A ticket that's been reassigned three times takes 4× longer and reopens 2× as often. Avium counts every hand-off.",
    tier: 'Team',

    problem: {
      heading: "The invisible tax",
      body: "Every time a ticket changes assignee, three things happen: the new person has to load the context, something gets lost in translation, and the calendar quietly advances. Two hand-offs is usually fine. Three is a smell. Five is a guarantee the ticket will be slow AND wrong.\n\nNobody tracks this. Jira shows assignee changes in the history view, one ticket at a time. Nobody aggregates. So a process problem that's visible in the data goes invisible in practice.",
    },

    manualMethod: {
      heading: "How teams try to spot hand-off problems",
      body: "Mostly they don't:",
      painPoints: [
        "Watching for tickets that 'feel hard' — every reassignment feels reasonable in isolation; the pattern only emerges in aggregate.",
        "Tracking 'specialty mismatch' — assuming hand-offs are because the wrong person got it first. Sometimes true, often a process issue.",
        "Calling them 'pickups' instead of hand-offs — semantic dodge, same problem.",
        "Asking the engineer who finally finished the ticket — they're the wrong person to ask; they only saw one leg of the relay.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes hand-off cost',
      body: "Avium reads the ticket field change history and counts assignee transitions per ticket.",
      mechanics: [
        "Hand-offs: number of times the assignee field changed during the ticket's lifecycle. 0 = same person start to finish; 4+ = problematic.",
        "Hand-off rate: average hand-offs per ticket across a window. Trends matter more than absolutes — a team that climbs from 1.2 to 2.5 is degrading even if no individual ticket looks bad.",
        "Correlation with cycle time + reopen rate: Avium internal models show >2 hand-offs roughly doubles average cycle time and increases reopen risk by ~80%. Visible on the Signal card.",
        "Per-ticket detail: the high-cost tickets, the people involved, and which transitions happened mid-sprint vs at planning.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "When your team complains 'work keeps moving around,' this is the number that names the pattern." },
      { role: 'Agile coaches', benefit: "Hand-off cost is one of the top three leading indicators of cycle-time pain. Show the trend, then coach the team to either reduce specialization or improve the hand-off protocol." },
      { role: 'Engineering managers', benefit: "Identifies the engineers who are picking up reassigned work disproportionately. Often the most senior; often the most under-credited. 1:1 input." },
      { role: 'VPs of engineering', benefit: "Hand-off cost climbing across an org usually means the work-allocation model has broken down. Surfaces a structural pattern before it shows up in attrition data." },
    ],

    cta: {
      heading: 'See your team\'s hand-off cost',
      body: "Avium reads your work management tool's assignee change history (Jira today, more integrations on the way) and surfaces hand-off cost on connection. Trend tracking + per-team rollup on Team; AI Briefing narrative on Business.",
    },

    relatedSlugs: ['flow-efficiency', 'reopen-rate', 'ping-pong-detection'],
  },

  'workflow-sankey': {
    slug: 'workflow-sankey',
    heroVisual: 'workflow-flow',
    signalName: 'Workflow Sankey',
    pageTitle: 'Workflow Sankey — how your tickets actually flow vs. how you think they do',
    metaDescription: "Avium Signals process-mines your Jira workflow — or any work management tool — into a Sankey diagram showing the real paths tickets take. Reveals shortcuts, dead ends, and the most-walked routes.",
    tagline: "Your Jira workflow on paper has 6 statuses. In practice it has 23 transitions, 4 of them backwards. Avium draws the picture nobody else does.",
    tier: 'Team',

    problem: {
      heading: "The map vs the territory",
      body: "Every team has a Jira workflow diagram somewhere. To Do → In Progress → Review → QA → Done. Clean. Linear.\n\nThe actual paths tickets take don't look like that. Tickets jump from In Progress straight to Done (no review). Tickets bounce from Review back to In Progress. Tickets sit in QA for a week then move to a status nobody documented. The workflow on paper bears no relation to the workflow in fact.",
    },

    manualMethod: {
      heading: "How teams try to understand their workflow",
      body: "Without a process-mining tool, you can't really:",
      painPoints: [
        "Asking the team how the workflow works — gets you the textbook version, not the actual one.",
        "Inspecting Jira transitions in the workflow editor — shows you what's POSSIBLE, not what's HAPPENING.",
        "Building a Sankey by hand from sprint reports — possible if you have 4 hours and a data analyst; impossible weekly.",
        "Enterprise process-mining tools (Celonis, Apromore) — built for compliance teams, priced for procurement departments.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes the workflow Sankey',
      body: "Avium reads every ticket status transition in your data and aggregates them into a flow diagram.",
      mechanics: [
        "Nodes: each unique Jira status that tickets actually entered (not just the ones the workflow editor allows).",
        "Edges: each transition between statuses, weighted by how many tickets walked that path.",
        "Backward edges flagged in red: a path from Done back to In Progress is a reopen; a path from Review back to In Progress is a rejected handback. Both are usually invisible in the standard reports.",
        "Phantom statuses: statuses that exist in your Jira but no ticket has been in for 90+ days. The workflow is more complex than it needs to be.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "First time you see this you'll spot at least one transition that shouldn't be happening. The Sankey makes it impossible to ignore." },
      { role: 'Agile coaches', benefit: "Process-mining was historically a six-figure software purchase. Now it's a tab in Avium. Show the team their actual workflow; the conversation about cleanup writes itself." },
      { role: 'Engineering managers', benefit: "Identifies the steps your team is skipping (often: code review). Sometimes intentional; sometimes a discipline problem." },
      { role: 'VPs of engineering', benefit: "Compare workflow shapes across teams. Teams whose Sankey looks like spaghetti consistently underperform teams whose Sankey looks linear." },
    ],

    cta: {
      heading: 'See your real workflow',
      body: "Once your work management tool is connected (Jira today, more integrations on the way), Avium renders the Sankey within seconds. Available on Team; the AI Briefing layer on Business can interpret the diagram in plain English ('your team is skipping review on 40% of tickets').",
    },

    relatedSlugs: ['ping-pong-detection', 'flow-efficiency', 'time-in-status'],
  },

  'ping-pong-detection': {
    slug: 'ping-pong-detection',
    heroVisual: 'workflow-flow',
    signalName: 'Ping-Pong Detection',
    pageTitle: 'Ping-Pong Detection — find tickets bouncing between people or statuses',
    metaDescription: "Avium Signals detects tickets bouncing between two people or two statuses — the hidden retro topic that's visible in your Jira data (or any work management tool) but not in your reports.",
    tagline: "Some tickets go forward. Some go in circles. Avium tells you which.",
    tier: 'Team',

    problem: {
      heading: "The retro topic that never comes up",
      body: "A ticket bounces from Alex to Sarah and back. Alex thought it was Sarah's; Sarah sent it back; Alex sent it back again. Each individual hand-off feels reasonable. The aggregate is a ticket that's spent a week passing back and forth and nobody has owned.\n\nOr: a ticket goes from In Progress to Review, back to In Progress, back to Review, back to In Progress. The team doesn't have language for that. Nobody complains. The ticket eventually closes, twice as slowly as it should have.",
    },

    manualMethod: {
      heading: "How teams try to catch ping-pong today",
      body: "They mostly don't. The patterns are invisible unless someone aggregates:",
      painPoints: [
        "Engineers feel it — 'this ticket keeps coming back to me' — but never aggregate. Anecdote, not pattern.",
        "Retros where someone says 'we had a few weird tickets this sprint' and the conversation moves on.",
        "Tracking reassignment counts via Jira's history view, one ticket at a time. Doesn't scale.",
        "Specialty matrices that assume hand-offs are skill-based when they're often process-based.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals detects ping-pong',
      body: "Avium looks for cycles in the assignee + status transition history of each ticket.",
      mechanics: [
        "Person ping-pong: assignee changes A → B → A (or longer cycles). Two complete cycles in the same ticket = ping-pong fired.",
        "Status ping-pong: status changes In Progress → Review → In Progress → Review. Same logic, different field.",
        "Severity: longer cycle, more cycles, longer total dwell = higher severity. Surfaced on the Insights page as a Signal card.",
        "Per-ticket details: who's involved, which transitions, when they happened. The retro material is the list itself.",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "Catch the pattern at the standup AFTER the first cycle, not the retro AFTER the third." },
      { role: 'Agile coaches', benefit: "One of the most diagnostic anti-patterns of an unhealthy hand-off culture. The number makes it concrete." },
      { role: 'Engineering managers', benefit: "Frame the 1:1 with the engineer who keeps receiving the bounce-backs. They usually know but don't have language for it." },
      { role: 'VPs of engineering', benefit: "If ping-pong is climbing across multiple teams, the structural cause is usually a definition-of-done erosion or a missing role (no QA, no tech lead, no PM coverage)." },
    ],

    cta: {
      heading: 'See where your work is going in circles',
      body: "Free tier surfaces ping-pong tickets once your work management tool is connected (Jira today, more integrations on the way). The pattern detection runs on every load; the AI Briefing on Business adds the 'why this is happening' narrative.",
    },

    relatedSlugs: ['hand-off-cost', 'workflow-sankey', 'reopen-rate'],
  },

  'time-in-status': {
    slug: 'time-in-status',
    heroVisual: 'workflow-flow',
    signalName: 'Time in Status',
    pageTitle: 'Time in Status — where work actually sits in your workflow',
    metaDescription: "Avium Signals computes per-phase dwell time across your tickets. Surfaces where work is piling up — code review queues, QA backlog, decision queues — with a list of currently-stuck tickets.",
    tagline: "Your team isn't slow. Your tickets are sitting somewhere. Avium tells you which somewhere.",
    tier: 'Team',

    problem: {
      heading: "Where the time actually goes",
      body: "A ticket takes 9 days from start to ship. The team is asked to be faster. They work harder; the time doesn't change. Because the 9 days included 4 days in code review queue, 2 days in QA queue, and 3 days of actual work. Engineers working harder makes the 3-day portion shorter and changes nothing else.\n\nTime-in-status is the diagnostic. The bottleneck is named. Then the right intervention happens — change the review SLA, add a second reviewer, automate the QA step — instead of a team motivational speech.",
    },

    manualMethod: {
      heading: "How teams try to read time-in-status today",
      body: "Jira hints at this but doesn't put it together:",
      painPoints: [
        "Jira's Control Chart shows cycle time but doesn't break it down by phase.",
        "Cycle Time + Lead Time reports exist but require enterprise plugins to slice usefully.",
        "Spreadsheet exports of ticket history — possible, painful, never repeated.",
        "Looking at the in-progress column 'feels heavy' — descriptive, not diagnostic.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium Signals computes time in status',
      body: "Avium reads every status transition and computes dwell time per phase across the org's tickets.",
      mechanics: [
        "Average dwell per phase: across the last N closed sprints, mean time tickets spent in each phase. Read top-to-bottom of your workflow.",
        "Median dwell per phase: less sensitive to outliers; often the better summary.",
        "Currently stuck list: every open ticket sorted by current-phase dwell. The longest-stuck tickets are usually the right standup discussion.",
        "Trend: is dwell in a given phase climbing over time? Often the first signal of a process problem (e.g., one reviewer leaving makes review-queue dwell climb).",
      ],
    },

    forWhom: [
      { role: 'Scrum masters', benefit: "The right standup conversation is rarely 'are we on track' — it's 'why is this ticket stuck.' Time-in-status surfaces the stuck list." },
      { role: 'Agile coaches', benefit: "Lean coaching with data. Targeted process changes — 'we need to cut review-queue time by half' — beats general 'we need to be faster' nudges." },
      { role: 'Engineering managers', benefit: "Diagnose your bottleneck before you intervene. Adding people to a team whose bottleneck is review doesn't help. Knowing this beforehand saves a quarter." },
      { role: 'VPs of engineering', benefit: "Compare time-in-status across teams. The team whose review queue is 5 days isn't the same team as the one whose review queue is 1 day, even if cycle time matches." },
    ],

    cta: {
      heading: 'See where your tickets sit',
      body: "Avium computes time-in-status as soon as your work management tool is connected (Jira today, more integrations on the way). Per-phase breakdown + stuck-ticket list on Team; the AI Briefing's interpretation ('your code review queue has grown 40% over two months') on Business.",
    },

    relatedSlugs: ['flow-efficiency', 'workflow-sankey', 'blocked-and-stale'],
  },

  'lead-time': {
    slug: 'lead-time',
    signalName: 'Lead Time',
    pageTitle: 'Lead Time — how long work really takes, from commit to done',
    metaDescription: "Avium measures true lead time from your Jira data — or any work management tool — with a configurable start phase and P50/P90 distribution. Stop answering \"when will it be done\" from gut feel.",
    tagline: "\"How long does work take here?\" should have a real answer, not a shrug. Avium measures lead time from the gate you actually commit at — through to done — across every ticket.",
    tier: 'Team',
    heroVisual: 'lead-time',

    problem: {
      heading: 'The question every stakeholder asks — and nobody can answer',
      body: "\"If we start this today, when will it be done?\" Everyone asks it; almost nobody answers it with data. The honest answer is buried in your tickets — when each one actually started, when it actually shipped — but it's never aggregated, so you answer from gut feel and recent memory. The estimate misses, and the next planning meeting runs on the same vibes.\n\nThe data to answer it precisely has been in your tracker the whole time. It just never got measured the same way twice.",
    },

    manualMethod: {
      heading: 'How teams try to measure lead time today',
      body: 'Every approach either measures the wrong span or hides the part that hurts:',
      painPoints: [
        "Created-to-resolved in a spreadsheet — but \"created\" isn't when you committed; tickets can sit in the backlog for months first, so the number is meaningless.",
        "A built-in chart that hardcodes the start at ticket creation — no way to say \"measure from when we actually pulled it into the sprint.\"",
        "Eyeballing a few recent tickets and calling them representative — until one slow outlier blows up the release and nobody saw it coming.",
        "An average with no distribution — \"about two weeks\" hides that 1 in 10 takes six. Stakeholders feel the P90, not the mean.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium measures lead time',
      body: 'Avium reads your real transition history and computes it the same way every time:',
      mechanics: [
        "Configurable start phase — measure from the gate your team actually commits at (Backlog Refined, Sprint Committed, Ready for Dev), not a hardcoded \"Created.\" Different teams commit at different gates; each picks its own.",
        "True \"done\" — uses the canonical resolution date, falling back to the first transition into a Done-category status, so closed-then-reopened work is measured honestly.",
        "Distribution, not just an average — P50 and P90, so you can say \"half ship in 8 days, 9 in 10 within 20\" — the number stakeholders actually feel.",
        "Computed from your real data (Jira today, or any work management tool) the moment it's connected — no manual export; recomputes as data changes.",
      ],
    },

    forWhom: [
      { role: 'Delivery leads & PMOs', benefit: 'A real, defensible answer to \"when will it be done\" — and the P90 to set expectations honestly instead of optimistically.' },
      { role: 'Engineering managers', benefit: 'Spot lead-time creep early — when the trend climbs, your process is slowing before the misses start.' },
      { role: 'Scrum masters', benefit: "Bring a distribution to retro instead of an anecdote — \"our tail got longer this quarter\" is a conversation; \"it felt slow\" isn't." },
    ],

    cta: {
      heading: 'See your real lead time in Avium',
      body: "Free tier connects your work management tool (Jira today, more integrations on the way) and computes lead time across your tickets. The configurable start phase + the distribution view are on Team; the AI Briefing's plain-English read ('your lead-time tail grew 40% this quarter') is on Business.",
    },

    relatedSlugs: ['cycle-time', 'time-in-status', 'flow-efficiency'],
  },

  'cycle-time': {
    slug: 'cycle-time',
    signalName: 'Cycle Time',
    pageTitle: 'Cycle Time — how long active work actually takes',
    metaDescription: "Avium measures true cycle time — first active work to done — from your Jira data, or any work management tool, with P50/P90. The honest read on how fast work moves once it starts.",
    tagline: "Lead time answers \"how long from commit to done.\" Cycle time answers \"how long once we actually start.\" Avium measures both from your real history — no guessing.",
    tier: 'Team',
    heroVisual: 'cycle-time',

    problem: {
      heading: "\"It's in progress\" tells you nothing about how long it'll take",
      body: "A ticket moves to In Progress and… time passes. Is that normal? Slow? You don't know, because \"normal\" was never measured. Some tickets fly through in two days; some grind for three weeks, and the only signal is a vague sense that this one's been open a while. By the time it's obviously stuck, the sprint has already absorbed the hit.\n\nCycle time — the clock from first active work to done — is the cleanest read on how fast work moves once it starts. It's sitting in your transition history, unmeasured.",
    },

    manualMethod: {
      heading: 'How teams approximate cycle time today',
      body: 'The common proxies all measure the wrong thing:',
      painPoints: [
        "Days between \"created\" and \"closed\" — which includes all the backlog-sitting time and isn't cycle time at all.",
        "A control chart per board — fine for one team, one board, and useless the moment you want it across teams or filtered by issue type.",
        "Story points as a stand-in for time — but points aren't time; a 3-pointer and a 5-pointer routinely take the same week.",
        "No outlier visibility — the average looks fine while a handful of multi-week tickets quietly define your release risk.",
      ],
    },

    aviumApproach: {
      heading: 'How Avium measures cycle time',
      body: 'Avium clocks only the active span, from your real history:',
      mechanics: [
        "The clock starts at first active work — the first transition into an in-progress status, not ticket creation — so backlog-sitting time doesn't pollute the number.",
        "The clock stops at done — canonical resolution, with a Done-category fallback — and reopened work is counted honestly.",
        "P50 and P90 distribution per team / issue type, so the slow tail is visible, not hidden behind an average.",
        "Derived from your real transition history (Jira today, or any work management tool) automatically — recomputes as work moves, no manual tracking.",
      ],
    },

    forWhom: [
      { role: 'Engineering managers', benefit: "Know what \"normal\" actually is, so \"this one's taking a while\" becomes a number you can act on, not a hunch." },
      { role: 'Scrum masters', benefit: "Catch the slow tail mid-sprint — the tickets drifting past your typical cycle time are tomorrow's spillover." },
      { role: 'Delivery leads & PMOs', benefit: 'Pair cycle time with lead time to see where the delay lives — in the doing, or in the waiting before work starts.' },
    ],

    cta: {
      heading: 'See your real cycle time in Avium',
      body: "Free tier connects your work management tool (Jira today, more integrations on the way) and computes cycle time from first active work to done. Distribution + per-team / issue-type breakdowns on Team; the AI Briefing's interpretation on Business.",
    },

    relatedSlugs: ['lead-time', 'time-in-status', 'flow-efficiency'],
  },
};

/** Lookup helper — null if the slug isn't yet content-backed. */
export function getSignalContent(slug: string | undefined): SignalLearnContent | null {
  if (!slug) return null;
  return SIGNAL_LEARN_CONTENT[slug] ?? null;
}
